import React, { useEffect, useState, useRef } from "react";
import { listMatchSummariesFromDb, MatchListSummary, deleteMatchFromDb, restoreAbandonedMatch } from "../store/supabaseMatches";
import { useAuth } from "../contexts/AuthContext";
import { normalizeStatus } from "../utils/matchStatus";
import TeamLogo from "../components/broadcast/TeamLogo";

interface MyMatchesProps {
  onClose: () => void;
  onViewMatch: (matchId: string) => void;
  initialCategory?: CategoryKey | null;
}

const MAX_PER_SECTION = 3;
const LOAD_LIMIT = 100;
const matchesCache = new Map<string, { data: MatchListSummary[]; timestamp: number }>();
const CACHE_DURATION = 30000;

function isAbortError(err: unknown): boolean {
  return err instanceof Error && (err.name === "AbortError" || err.message === "signal is aborted without reason");
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function getFormatDisplay(format: string): string {
  switch (format) {
    case "INDOOR": return "Indoor";
    case "T20": return "T20";
    case "ODI": return "50 Overs";
    case "TEST": return "Test";
    default: return format;
  }
}

function formatScheduledDate(iso: string | null | undefined): string {
  if (!iso) return "Not scheduled";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "Not scheduled";
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  hour: "2-digit",
    minute: "2-digit",
  hour12: false,
  timeZone: "Europe/London",
  }).replace(/,/g, " ·");
}

function getMatchDate(match: MatchListSummary): string {
  const normalized = normalizeStatus(match.status);
  if (normalized === "completed" && match.completedAt) {
    return formatScheduledDate(match.completedAt);
  }
  if (match.scheduledAt) {
    return formatScheduledDate(match.scheduledAt);
  }
  if (normalized === "completed" && match.updatedAt) {
    return formatScheduledDate(match.updatedAt);
  }
  return "Not scheduled";
}

type CategoryKey = "ongoing" | "upcoming" | "completed";

const MyMatches: React.FC<MyMatchesProps> = ({ onClose, onViewMatch, initialCategory }) => {
  const { user } = useAuth();
  const [matches, setMatches] = useState<MatchListSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [viewAllCategory, setViewAllCategory] = useState<CategoryKey | null>(initialCategory ?? null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    loadData(false);
    return () => { mountedRef.current = false; };
  }, [user]);

  async function loadData(useCache: boolean = true) {
    if (!user) {
      if (mountedRef.current) setLoading(false);
      return;
    }
    const cacheKey = `matches_${user.id}`;
    const cached = matchesCache.get(cacheKey);
    const now = Date.now();
    if (useCache && cached && (now - cached.timestamp) < CACHE_DURATION) {
      if (mountedRef.current) {
        setMatches(cached.data);
        setLoading(false);
      }
      return;
    }
    try {
      if (mountedRef.current) { setLoading(true); setError(null); }
      const allMatches = await listMatchSummariesFromDb(LOAD_LIMIT);
      if (!mountedRef.current) return;
      const safeMatches = allMatches ?? [];
      setMatches(safeMatches);
      matchesCache.set(cacheKey, { data: safeMatches, timestamp: now });
    } catch (err) {
      if (!mountedRef.current) return;
      if (isAbortError(err)) return;
      const errorMessage = err instanceof Error ? err.message : "Unknown error loading matches";
      setError(errorMessage);
      setMatches([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }

  async function handleRefresh() {
    if (!user) return;
    if (mountedRef.current) setRefreshing(true);
    const cacheKey = `matches_${user.id}`;
    matchesCache.delete(cacheKey);
    try {
      const allMatches = await listMatchSummariesFromDb(LOAD_LIMIT);
      if (!mountedRef.current) return;
      const safeMatches = allMatches ?? [];
      setMatches(safeMatches);
      matchesCache.set(cacheKey, { data: safeMatches, timestamp: Date.now() });
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      if (isAbortError(err)) return;
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setError(errorMessage);
      setMatches([]);
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  }

  async function handleDelete(id: string) {
    if (deleteConfirmId === id) {
      const result = await deleteMatchFromDb(id);
      if (result.success) {
        if (user) {
          const cacheKey = `matches_${user.id}`;
          matchesCache.delete(cacheKey);
        }
        setMatches(prev => prev.filter(m => m.id !== id));
      } else {
        alert("Failed to delete match: " + (result.error || "Unknown error"));
      }
      setDeleteConfirmId(null);
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  }

  async function handleRestore(id: string) {
    const result = await restoreAbandonedMatch(id);
    if (!result.success) {
      alert("Failed to restore match: " + (result.error || "Unknown error"));
      return;
    }
    if (user) {
      const cacheKey = `matches_${user.id}`;
      matchesCache.delete(cacheKey);
    }
    await handleRefresh();
  }

  const hasScoringActivity = (m: MatchListSummary): boolean => {
    if (m.hasActivity) return true;
    if (typeof m.legalBalls === "number" && m.legalBalls > 0) return true;
    return false;
  };

  const sortedMatches = [...matches].sort((a, b) => {
    const isALive = normalizeStatus(a.status) === "live";
    const isBLive = normalizeStatus(b.status) === "live";
    if (isALive && !isBLive) return -1;
    if (!isALive && isBLive) return 1;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const ongoingMatches = sortedMatches.filter(m => {
    const s = normalizeStatus(m.status);
    if (s === "live") return true;
    if (s === "abandoned" && hasScoringActivity(m)) return true;
    return false;
  });

  const completedMatches = sortedMatches
    .filter(m => normalizeStatus(m.status) === "completed")
    .sort((a, b) => {
      const aTime = a.completedAt ? new Date(a.completedAt).getTime() : new Date(a.updatedAt).getTime();
      const bTime = b.completedAt ? new Date(b.completedAt).getTime() : new Date(b.updatedAt).getTime();
      return bTime - aTime;
    });

  const upcomingMatches = sortedMatches
    .filter(m => {
      const s = normalizeStatus(m.status);
      if (s === "draft") return true;
      if (s === "abandoned" && !hasScoringActivity(m)) return true;
      return false;
    })
    .sort((a, b) => {
      const aTime = a.scheduledAt ? new Date(a.scheduledAt).getTime() : Infinity;
      const bTime = b.scheduledAt ? new Date(b.scheduledAt).getTime() : Infinity;
      return aTime - bTime;
    });

  const sections: { key: CategoryKey; label: string; color: string; items: MatchListSummary[] }[] = [
    { key: "ongoing", label: "Ongoing Matches", color: "text-green-400", items: ongoingMatches },
    { key: "upcoming", label: "Upcoming Matches", color: "text-blue-400", items: upcomingMatches },
    { key: "completed", label: "Completed Matches", color: "text-slate-400", items: completedMatches },
  ];

  // ── Loading ──
  if (loading && matches.length === 0) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#012b1b] via-[#064428] to-[#012b1b] z-50 overflow-y-auto pb-20">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-white">My Matches</h1>
            <button onClick={onClose} className="text-green-400 hover:text-white text-2xl">×</button>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#012b1b] via-[#064428] to-[#012b1b] z-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-6 mb-4">
            <h2 className="text-xl font-bold text-white mb-2 text-center">Error Loading Matches</h2>
            <p className="text-sm text-slate-300 mb-4 text-center">{error}</p>
            <button onClick={() => loadData(false)} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-colors">Retry</button>
          </div>
          <button onClick={onClose} className="w-full bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 px-4 rounded-lg transition-colors">Back to Home</button>
        </div>
      </div>
    );
  }

  // ── Not signed in ──
  if (!user) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#012b1b] via-[#064428] to-[#012b1b] z-50 overflow-y-auto pb-20">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-white">My Matches</h1>
            <button onClick={onClose} className="text-green-400 hover:text-white text-2xl">×</button>
          </div>
          <div className="bg-[#064428]/40 rounded-xl p-8 text-center border border-[#0b5c33]/50">
            <p className="text-green-300">Please sign in to view your matches</p>
          </div>
        </div>
      </div>
    );
  }

  // ── View All page ──
  if (viewAllCategory) {
    const section = sections.find(s => s.key === viewAllCategory)!;
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#012b1b] via-[#064428] to-[#012b1b] z-50 overflow-y-auto pb-20">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button onClick={() => setViewAllCategory(null)} className="text-green-400 hover:text-white text-2xl leading-none">←</button>
              <h1 className="text-2xl font-bold text-white">{section.label}</h1>
            </div>
            <button onClick={onClose} className="text-green-400 hover:text-white text-2xl">×</button>
          </div>
          <div className="space-y-4">
            {section.items.map(match => (
              <MatchCard
                key={match.id}
                match={match}
                onView={onViewMatch}
                onDelete={handleDelete}
                onRestore={handleRestore}
                hasActivity={hasScoringActivity(match)}
                deleteConfirmId={deleteConfirmId}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Main overview ──
  const visibleSections = sections.filter(s => s.items.length > 0);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-[#012b1b] via-[#064428] to-[#012b1b] z-50 overflow-y-auto pb-20">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-white">My Matches</h1>
          <div className="flex items-center gap-3">
            <button onClick={handleRefresh} disabled={refreshing} className="text-green-400 hover:text-white transition-colors disabled:opacity-50" title="Refresh matches">
              <svg className={`w-6 h-6 ${refreshing ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button onClick={onClose} className="text-green-400 hover:text-white text-2xl">×</button>
          </div>
        </div>

        {visibleSections.length === 0 ? (
          <div className="bg-[#064428]/40 rounded-xl p-12 text-center border border-[#0b5c33]/50">
            <div className="text-6xl mb-4">🏏</div>
            <h3 className="text-xl font-semibold mb-2 text-white">No Matches Yet</h3>
            <p className="text-green-300">Tap the red cricket ball to start your first match</p>
          </div>
        ) : (
          <div className="space-y-8">
            {visibleSections.map(section => (
              <div key={section.key}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`text-xl font-bold ${section.color}`}>{section.label}</h2>
                  {section.items.length > MAX_PER_SECTION && (
                    <button
                      onClick={() => setViewAllCategory(section.key)}
                      className="text-green-400 hover:text-green-300 text-sm font-semibold transition-colors"
                    >
                      View All →
                    </button>
                  )}
                </div>
                <div className="space-y-4">
                  {section.items.slice(0, MAX_PER_SECTION).map(match => (
                    <MatchCard
                      key={match.id}
                      match={match}
                      onView={onViewMatch}
                      onDelete={handleDelete}
                      onRestore={handleRestore}
                      hasActivity={hasScoringActivity(match)}
                      deleteConfirmId={deleteConfirmId}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════
// Match Card
// ═══════════════════════════════════════════════

function MatchCard({
  match,
  onView,
  onDelete,
  onRestore,
  hasActivity,
  deleteConfirmId,
}: {
  match: MatchListSummary;
  onView: (id: string) => void;
  onDelete: (id: string) => void;
  onRestore?: (id: string) => void;
  hasActivity?: boolean;
  deleteConfirmId: string | null;
}) {
  const normalized = normalizeStatus(match.status);
  const isAbandoned = normalized === "abandoned";
  const isLive = normalized === "live";
  const isCompleted = normalized === "completed";

  const badgeLabel = isAbandoned
    ? (hasActivity ? "Abandoned (Interrupted)" : "Abandoned (Not Started)")
    : isLive ? "LIVE" : isCompleted ? "Completed" : "Upcoming";

  const badgeColor = isLive
    ? "bg-red-500/20 text-red-300 border border-red-500/40"
    : isCompleted
    ? "bg-slate-600/30 text-slate-300 border border-slate-500/40"
    : isAbandoned
    ? "bg-orange-500/20 text-orange-300 border border-orange-500/40"
    : "bg-blue-500/20 text-blue-300 border border-blue-500/40";

  const dateLabel = getMatchDate(match);
  const venueLabel = match.venue || "";

  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-800/60 rounded-2xl border border-slate-700/60 shadow-lg overflow-hidden transition-all hover:border-slate-600">
      {/* Top row: format/mode + badge | date+time */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">{getFormatDisplay(match.format)}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${badgeColor}`}>
            {badgeLabel}
          </span>
        </div>
        <span className="text-xs text-slate-300 font-medium whitespace-nowrap ml-2">{dateLabel}</span>
      </div>

      {/* Logos + VS */}
      <div className="flex items-center justify-around px-4 py-4">
        <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
          <TeamLogoLarge logoUrl={match.teamALogoUrl} teamName={match.teamAName} />
          <span className="text-sm font-semibold text-white text-center break-words leading-tight max-w-[140px]">{match.teamAName || "Team A"}</span>
        </div>
        <div className="flex flex-col items-center px-2">
          <span className="text-lg font-black text-slate-500 tracking-wider">VS</span>
        </div>
        <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
          <TeamLogoLarge logoUrl={match.teamBLogoUrl} teamName={match.teamBName} />
          <span className="text-sm font-semibold text-white text-center break-words leading-tight max-w-[140px]">{match.teamBName || "Team B"}</span>
        </div>
      </div>

      {/* Venue */}
      {venueLabel && (
        <div className="flex items-center justify-center px-4 pb-2">
          <span className="text-xs text-slate-400 truncate">📍 {venueLabel}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 px-4 pb-3 pt-1">
        {isAbandoned && onRestore && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRestore(match.id); }}
            className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 px-4 py-2.5 rounded-lg font-bold text-sm transition-all shadow-md"
            style={{ minHeight: "44px" }}
            title={hasActivity ? "Restore to Live" : "Restore to Draft"}
          >
            Restore
          </button>
        )}
        {!isAbandoned && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onView(match.id); }}
            className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 active:from-blue-700 active:to-blue-800 px-4 py-2.5 rounded-lg font-bold text-sm transition-all shadow-md touch-manipulation select-none"
            style={{ WebkitTapHighlightColor: "transparent", cursor: "pointer", minHeight: "44px" }}
          >
            {isLive ? "Resume" : "View"}
          </button>
        )}
        <button
          onClick={() => onDelete(match.id)}
          className={`px-4 py-2.5 rounded-lg font-bold text-sm transition-all shadow-md ${deleteConfirmId === match.id ? "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600" : "bg-slate-700 hover:bg-slate-600 border border-slate-600"}`}
          style={{ minHeight: "44px" }}
        >
          {deleteConfirmId === match.id ? "Confirm?" : "Delete"}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// Large Team Logo (~90-100px)
// ═══════════════════════════════════════════════

function TeamLogoLarge({ logoUrl, teamName }: { logoUrl?: string | null; teamName: string }) {
  const size = 96;
  return (
    <div className="flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={`${teamName} logo`}
          className="w-full h-full object-contain"
          style={{ maxWidth: "100%", maxHeight: "100%" }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
            const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
            if (fallback) fallback.style.display = "flex";
          }}
        />
      ) : null}
      <div
        className="rounded-2xl bg-gradient-to-br from-emerald-800/60 to-emerald-900/60 border border-emerald-600/30 flex items-center justify-center font-bold text-emerald-200"
        style={{ width: "100%", height: "100%", fontSize: "1.75rem", display: logoUrl ? "none" : "flex" }}
      >
        {getInitials(teamName || "?")}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════
// Skeleton
// ═══════════════════════════════════════════════

function SkeletonCard() {
  return (
    <div className="bg-slate-800 rounded-2xl border border-slate-700/60 shadow-lg animate-pulse">
      <div className="flex justify-between px-4 pt-3 pb-2">
        <div className="h-4 bg-slate-700 rounded w-24"></div>
        <div className="h-4 bg-slate-700 rounded w-32"></div>
      </div>
      <div className="flex items-center justify-around px-4 py-6">
        <div className="flex flex-col items-center gap-2">
          <div className="rounded-2xl bg-slate-700" style={{ width: 96, height: 96 }}></div>
          <div className="h-3 bg-slate-700 rounded w-20"></div>
        </div>
        <div className="h-5 bg-slate-700 rounded w-8"></div>
        <div className="flex flex-col items-center gap-2">
          <div className="rounded-2xl bg-slate-700" style={{ width: 96, height: 96 }}></div>
          <div className="h-3 bg-slate-700 rounded w-20"></div>
        </div>
      </div>
      <div className="flex gap-2 px-4 pb-3">
        <div className="h-10 bg-slate-700 rounded flex-1"></div>
        <div className="h-10 bg-slate-700 rounded w-24"></div>
      </div>
    </div>
  );
}

export default MyMatches;
